/**
 * Shared UpCart cart-change plumbing.
 *
 * UpCart is an app embed with no event API to bind to, so the only signal that the cart changed is
 * its drawer markup re-rendering inside its shadow root. Both the free-bracelet and PWYW PDPs need
 * that signal, so the seam lives here rather than being copied into each.
 *
 * Everything below depends on UpCart internals — the `upCart` element id, its shadow root, the
 * `.upcart-cart` class, and how long its own Shopify call takes. When those change, fix them here.
 */

export function getCart() {
  return window
    .fetch('/cart.js')
    .then((response) => response.json())
    .then((data) => {
      return data;
    });
}

// Rejects on timeout rather than hanging, so callers can decide how to degrade.
export function waitForUpCartShadowRoot({ interval = 10, timeout = 10000 } = {}) {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    const poller = window.setInterval(() => {
      const upCart = document.getElementById('upCart');

      if (upCart && upCart.shadowRoot) {
        window.clearInterval(poller);
        resolve(upCart.shadowRoot);
        return;
      }

      if (Date.now() - startTime >= timeout) {
        window.clearInterval(poller);
        reject(new Error('Timed out waiting for upCart shadow root.'));
      }
    }, interval);
  });
}

/**
 * Calls handleUIUpdate with fresh cart state whenever UpCart re-renders its drawer.
 *
 * @param {Object} options
 * @param {Function} options.handleCartCallback - Returns a promise resolving to cart state.
 * @param {Function} options.handleUIUpdate - Receives the resolved cart state.
 * @param {Function} [options.onMutationStart] - Runs synchronously on every mutation, before the
 *   debounced fetch, so buy buttons can be disabled before the new cart state is known.
 */
export function monitorCartChange({ handleCartCallback, handleUIUpdate, onMutationStart }) {
  let debounceTimer = null;
  let requestId = 0;

  const observer = new MutationObserver(() => {
    // Increment immediately so any in-flight fetch from a prior debounce is invalidated
    requestId++;
    if (onMutationStart) {
      onMutationStart();
    }
    clearTimeout(debounceTimer);
    // 2000ms gives UPcart's Shopify API call time to complete before we fetch cart state,
    // preventing a stale fetch from firing between UPcart's loading and done mutation batches
    debounceTimer = setTimeout(() => {
      const myId = requestId;
      handleCartCallback()
        .then((currentCartState) => {
          // Ignore if another mutation fired while this fetch was in flight
          if (myId !== requestId) {
            return;
          }
          handleUIUpdate(currentCartState);
        })
        .catch((error) => {
          console.error('Error fetching cart data:', error);
        });
    }, 2000);
  });

  const upCart = document.getElementById('upCart');
  // cartDrawer is inside upCart, so we need to get the cart drawer element from upCart
  // Element is in shadow root, so we need to use shadowRoot to get the cart drawer element
  if (!upCart) {
    console.error('upCart element not found. Unable to monitor cart changes.');
    return;
  }
  const cartDrawer = upCart.shadowRoot ? upCart.shadowRoot.querySelector('.upcart-cart') : null;
  if (cartDrawer) {
    observer.observe(cartDrawer, { childList: true, subtree: true });
  } else {
    console.error('Cart drawer element not found. Unable to monitor cart changes.');
  }
}
